import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, toConsumableArray as _toConsumableArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useMemo, useCallback } from 'react';
import '@walmart-web/livingdesign-components/index.esm.css';
import { DataTableCellStatus, DataTableCell, DataTableHeader, Caption, DataTableRow, DataTable, DataTableHead, DataTableBody } from '@walmart-web/livingdesign-components';
import '../Alert/Alert.js';
import '../Button/Button.js';
import '../Button/ButtonGroup.js';
import '../Radio/Radio.js';
import { Tag } from '../Tag/Tag.js';
import '../Checkbox/Checkbox.js';
import '../Chip/Chip.js';
import classNames from 'classnames';
import '../IconButton/IconButton.js';
import '@livingdesign/icons';
import '@livingdesign/tokens/dist/js/light/regular/components/Form';
import '../TextField/TextField.js';
import '../TabNavigation/TabNavigation.js';
import '../TabNavigation/TabNavigationItem.js';
import '../Modal/Modal.js';
import '@livingdesign/tokens/dist/js/light/regular/components/ErrorMessage';
import '../SearchBox/SearchBox.js';
import '../AutoComplete/AutoComplete.js';
import '../TextArea/TextArea.js';
import '../ProgressIndicator/ProgressIndicator.js';
import '../Panel/Panel.js';
import '../Calendar/locale/wmLocale.js';
import '../Calendar/locale/stdLocale.js';
import '../Calendar/context.js';
import 'dayjs';
import '../Calendar/RenderDays.js';
import '../Calendar/RenderWeeks.js';
import '../components/Popover/Popover.js';
import '../components/InfoUnderline/InfoUnderline.js';
import { Skeleton } from '../components/shared/Skeleton/Skeleton.js';
import '../CardActionArea/CardActionArea.js';
import '../HierarchySelector/HierarchySelector.js';
import '../Pagination/Pagination.js';
import '../DisplayTag/DisplayTag.js';
import '../PageHeader/PageHeader.js';
import '../Tooltip/Tooltip.js';
import '../ValueChange/ValueChange.js';
import '../OverlayHeader/OverlayHeader.js';
import styles from './Table.module.scss.js';
import { TableHeader } from './TableHeader.js';

var _excluded = ["columns", "handleSort", "sort", "data", "isLoading", "loadingSkeletonRows", "emptyDataMessage", "stickyHeader", "isError", "errorContent", "onRowClick", "selectedRowId"];
/**
 * Data Tables display a set of related information in rows and columns. They allow users to efficiently scan and compare large amounts of data and, optionally, to take action on that data
 * 
 * ### Content
 * 
 * #### Column Definition and Data
 * 
 * The table accepts column definiton to structure the table and data to populate within the table. 
 * 
 * The key of data should match the field in the column to render the data in the right column
 * 
 * ```tsx
 * import * as React from "react";
 * import { Table } from "@walmart-dataventures/shared-components";
 * 
 * export default () => {
 *  return (
 *      <Table 
 *          columns={[{
 *          columnIndex: 0,
            field: 'id',
            header: 'ID',
            type: 'numeric'
            },
            {
            columnIndex: 1,
            field: 'name',
            header: 'Name',
            type: 'alphanumeric'
            }]}
            data={[{
                id: 1658943,
                name: 'Cesar_ChickenBox'
            },
            {
                id: 1658942,
                name: 'Toothbrushes'
            }]}
            isLoading={false}
        />
 *  )
 * }
 * 
 * ```
 * 
 * ### Features
 * 
 * #### Sorting
 * 
 * Sorting can be enabled on individual columns by passing `sort: true` in the column definition
 * 
 * Sort State can be managed by passing object with attribute and order to the sort prop and passing a callback funciton in `handleSort` prop
 * 
 * Example 
 * 
 * ```tsx
 * <Table
 *  sort={
            attribute: 'name',
            order: 'ascending',
        }
    handleSort={(attribute: string)=>{//handle sort logic}}
    {...restProps}
    />
 * ```
 * 
 * #### Fixed Column 
 * 
 * Fixed column can be enabled on 1st and last columns when the table has many number of columns and we need to enable scroll 
 * 
 * `fixedColumn: true` should be passed in the column definition
 * 
 * Do not enable fixed column in any other column except 1st and last column
 * 
 * #### Column Type - Status
 * 
 * When column type is set to status, `getStatusColor` prop can be used to send the status color using the row data
 * 
 * #### Column Type - header
 * 
 * when cloumn type is set to header, that column's data will be rendered as DataHeader.
 * if we  want to hide column header text, we can pass `showColumnHeader: false` in the column definition
 * we can make this column sticky by setting `fixedColumn: true`
 * 
 * #### Column Type - Actions 
 * 
 * When column type is set to actions, we need to pass a function that returns JSX element to render the element in the cell. 
 * 
 * Example
 * 
 * ```tsx
 * {
        header: 'Name',
        field: 'name',
        type: 'action',
        sort: true,
        fixedColumn: true,
        actionElement: (row) => (
            <Button
                variant="tertiary"
                onClick={() => {
                    alert(row?.name);
                }}
            >
                {row?.name}
            </Button>
        ),
    },
 * ```
 * 
 * #### Loading 
 * 
 * When api is still in pending state, `isLoading` can be set to true to display skeleton as loading indicator. 
 * Use `loadingSkeletonRows` depending on the number of skeleton rows required to display
 * 
 * Example 
 * 
 * ```tsx
 * <Table
 *    data=[{},{},{}]
 *    isLoading={true}
 *    loadingSkeletonRows={5}
      {...restProps}
    />
    ```
 * 
 * 
 * #### Sticky header 
 * 
 * When the table has large amounts of data, and user needs to scroll, setting `stickyHeader` to 'true' will make the header stick to the top
 * 
 * #### No data message 
 * 
 * When there is no data to be displayed, table can display a message for users by setting `emptyDataMessage` prop
 * 
 */
var Table = function Table(_ref) {
  var columns = _ref.columns,
    handleSort = _ref.handleSort,
    sort = _ref.sort,
    data = _ref.data,
    isLoading = _ref.isLoading,
    _ref$loadingSkeletonR = _ref.loadingSkeletonRows,
    loadingSkeletonRows = _ref$loadingSkeletonR === void 0 ? 3 : _ref$loadingSkeletonR,
    emptyDataMessage = _ref.emptyDataMessage,
    _ref$stickyHeader = _ref.stickyHeader,
    stickyHeader = _ref$stickyHeader === void 0 ? false : _ref$stickyHeader,
    _ref$isError = _ref.isError,
    isError = _ref$isError === void 0 ? false : _ref$isError,
    _ref$errorContent = _ref.errorContent,
    errorContent = _ref$errorContent === void 0 ? 'Error occurred' : _ref$errorContent,
    onRowClick = _ref.onRowClick,
    _ref$selectedRowId = _ref.selectedRowId,
    selectedRowId = _ref$selectedRowId === void 0 ? '' : _ref$selectedRowId,
    rest = _objectWithoutProperties(_ref, _excluded);
  var dataTestCy = 'table-selector';
  var columnClasses = function columnClasses(item) {
    return classNames(item !== null && item !== void 0 && item.fixedColumn && (item === null || item === void 0 ? void 0 : item.type) === 'header' ? styles.fixedColumnBody : '', item !== null && item !== void 0 && item.fixedColumn && (item === null || item === void 0 ? void 0 : item.type) !== 'header' ? "".concat(styles.fixedColumnBody, " ").concat(styles.backgroundColorWhite) : '', item !== null && item !== void 0 && item.showColumnDivider ? styles.columnDivider : '');
  };
  var getTableHeader = useMemo(function () {
    return columns.map(function (columnDef) {
      var sortProps = {
        onSort: function onSort() {
          return handleSort === null || handleSort === void 0 ? void 0 : handleSort(columnDef === null || columnDef === void 0 ? void 0 : columnDef.field);
        },
        sort: (columnDef === null || columnDef === void 0 ? void 0 : columnDef.field.toLowerCase()) === (sort === null || sort === void 0 ? void 0 : sort.attribute.toLowerCase()) ? sort === null || sort === void 0 ? void 0 : sort.order : 'none'
      };
      return /*#__PURE__*/React__default.createElement(TableHeader, _extends({
        key: columnDef.columnIndex,
        columnDef: columnDef
      }, (columnDef === null || columnDef === void 0 ? void 0 : columnDef.sort) && sortProps, {
        "data-test-cy": "".concat(columnDef === null || columnDef === void 0 ? void 0 : columnDef.header, "-header-selector")
      }));
    });
  }, [columns, sort, handleSort]);
  var getTableRow = useCallback(function (row) {
    return columns === null || columns === void 0 ? void 0 : columns.map(function (item) {
      var additionalInfoItem = "".concat(item.field, "_additionalInfo");
      if ((item === null || item === void 0 ? void 0 : item.type) === 'status') {
        var _item$getStatusColor;
        return /*#__PURE__*/React__default.createElement(DataTableCellStatus, {
          UNSAFE_className: columnClasses(item),
          key: "".concat(item === null || item === void 0 ? void 0 : item.columnIndex, "~").concat(row === null || row === void 0 ? void 0 : row.id)
        }, /*#__PURE__*/React__default.createElement(Tag, {
          color: item === null || item === void 0 || (_item$getStatusColor = item.getStatusColor) === null || _item$getStatusColor === void 0 ? void 0 : _item$getStatusColor.call(item, row),
          variant: "tertiary"
        }, row === null || row === void 0 ? void 0 : row[item === null || item === void 0 ? void 0 : item.field]));
      } else if ((item === null || item === void 0 ? void 0 : item.type) === 'custom') {
        var _item$customElement;
        return /*#__PURE__*/React__default.createElement(DataTableCell, {
          UNSAFE_className: columnClasses(item),
          key: "".concat(item === null || item === void 0 ? void 0 : item.columnIndex, "~").concat(row === null || row === void 0 ? void 0 : row.id)
        }, item === null || item === void 0 || (_item$customElement = item.customElement) === null || _item$customElement === void 0 ? void 0 : _item$customElement.call(item, row));
      } else if ((item === null || item === void 0 ? void 0 : item.type) === 'header') {
        return /*#__PURE__*/React__default.createElement(DataTableHeader, {
          UNSAFE_className: columnClasses(item),
          key: "".concat(item === null || item === void 0 ? void 0 : item.columnIndex, "~").concat(row === null || row === void 0 ? void 0 : row.id),
          role: "rowheader"
        }, row === null || row === void 0 ? void 0 : row[item === null || item === void 0 ? void 0 : item.field]);
      } else {
        return /*#__PURE__*/React__default.createElement(DataTableCell, {
          UNSAFE_className: columnClasses(item),
          key: "".concat(item === null || item === void 0 ? void 0 : item.columnIndex, "~").concat(row === null || row === void 0 ? void 0 : row.id)
        }, /*#__PURE__*/React__default.createElement("div", {
          className: styles.flexColumn
        }, /*#__PURE__*/React__default.createElement("div", {
          className: styles.content
        }, row === null || row === void 0 ? void 0 : row[item === null || item === void 0 ? void 0 : item.field]), row !== null && row !== void 0 && row[additionalInfoItem] ? /*#__PURE__*/React__default.createElement(Caption, {
          color: "gray100"
        }, row === null || row === void 0 ? void 0 : row[additionalInfoItem]) : null));
      }
    });
  }, [columns, columnClasses]);
  var Loading = function Loading() {
    return _toConsumableArray(Array(loadingSkeletonRows)).map(function (number, index) {
      return /*#__PURE__*/React__default.createElement(DataTableRow, {
        key: "".concat(number, "~").concat(index)
      }, columns === null || columns === void 0 ? void 0 : columns.map(function (item) {
        return /*#__PURE__*/React__default.createElement(DataTableCell, {
          key: item.columnIndex
        }, /*#__PURE__*/React__default.createElement(Skeleton, {
          height: "revert-layer",
          width: "auto",
          variant: "rounded"
        }));
      }));
    });
  };
  var handleRowClick = function handleRowClick(row, index) {
    onRowClick === null || onRowClick === void 0 || onRowClick(row, index);
  };
  var getTableRows = useMemo(function () {
    return data === null || data === void 0 ? void 0 : data.map(function (row, index) {
      return /*#__PURE__*/React__default.createElement(DataTableRow, {
        key: row === null || row === void 0 ? void 0 : row.id,
        UNSAFE_className: (row === null || row === void 0 ? void 0 : row.id) === selectedRowId ? styles.selected : '',
        onClick: function onClick() {
          return handleRowClick(row, index);
        }
      }, getTableRow(row));
    });
  }, [data, columns, selectedRowId, sort === null || sort === void 0 ? void 0 : sort.order]);
  var getFallbackContent = useCallback(function (content) {
    return /*#__PURE__*/React__default.createElement(DataTableRow, {
      color: "gray100"
    }, /*#__PURE__*/React__default.createElement(DataTableCell, {
      UNSAFE_className: styles.emptyMessageContainer,
      colSpan: columns.length
    }, content));
  }, [columns.length]);
  var getTableContent = useMemo(function () {
    if (isLoading) return /*#__PURE__*/React__default.createElement(Loading, null);
    if (isError && errorContent) return getFallbackContent(errorContent);
    if (!data.length) return getFallbackContent(emptyDataMessage);
    return getTableRows;
  }, [isLoading, isError, errorContent, data, emptyDataMessage, selectedRowId, sort === null || sort === void 0 ? void 0 : sort.order]);
  return /*#__PURE__*/React__default.createElement(DataTable, _extends({
    "data-test-cy": dataTestCy
  }, rest), /*#__PURE__*/React__default.createElement(DataTableHead, {
    UNSAFE_className: stickyHeader ? styles.fixedHeader : ''
  }, /*#__PURE__*/React__default.createElement(DataTableRow, null, getTableHeader)), /*#__PURE__*/React__default.createElement(DataTableBody, null, getTableContent));
};
Table.displayName = 'Table';

export { Table };
